/**
 * Merkmale-Reiter der Einstellungen (Aufgabe 12): Merkmalsdefinitionen
 * (Name, erlaubte Werte) anlegen und bearbeiten.
 *
 * Anders als der Softwarekatalog entstehen Merkmale NICHT beim Tippen in der
 * Kundenakte — sie werden bewusst hier gepflegt. Das Setzen eines Werts an
 * einem System bleibt für jeden Angemeldeten offen (Spec E6); dieser Reiter
 * regelt nur, WAS gesetzt werden kann.
 */
import { Alert, Badge, Button, Group, Modal, Paper, Skeleton, Stack, Table, Text, TagsInput, TextInput } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useAttributeDefs,
  useCreateAttributeDef,
  useUpdateAttributeDef,
  type AttributeDefRef,
} from "../../hooks/useMaintenance.js";

/**
 * `null` heißt „geschlossen"; ist ein Objekt gesetzt, ist der Dialog offen —
 * `target: null` darin ist der Anlegen-Fall, `target: AttributeDefRef` das
 * Bearbeiten. Ein einzelnes `AttributeDefRef | null`-State-Feld könnte
 * „geschlossen" und „Anlegen" nicht unterscheiden, deshalb die Hülle.
 */
interface AttributeDialogState {
  target: AttributeDefRef | null;
}

export function SystemAttributeDefsTab() {
  const { t } = useTranslation();
  const { data: defs, isLoading, isError } = useAttributeDefs();
  const [dialog, setDialog] = useState<AttributeDialogState | null>(null);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Text c="dimmed" size="sm">
          {t("pages.settings.attributes.subtitle")}
        </Text>
        <Button size="xs" onClick={() => setDialog({ target: null })}>
          {t("pages.settings.attributes.createButton")}
        </Button>
      </Group>

      <Paper p="md" withBorder>
        {isError ? (
          <Alert color="red" title={t("common.error")}>
            {t("common.errorLoading")}
          </Alert>
        ) : isLoading ? (
          <Skeleton height={200} />
        ) : (
          <Table highlightOnHover striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("pages.settings.attributes.tableName")}</Table.Th>
                <Table.Th>{t("pages.settings.attributes.tableAllowedValues")}</Table.Th>
                <Table.Th style={{ width: 110 }}>{t("pages.settings.attributes.tableUsage")}</Table.Th>
                <Table.Th style={{ width: 110 }}>{t("pages.settings.users.tableAction")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(defs ?? []).map((d) => (
                <Table.Tr key={d.id}>
                  <Table.Td>
                    <Text fw={500} size="sm">
                      {d.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {d.allowedValues.length === 0 ? (
                      <Text c="dimmed" size="xs">
                        {t("pages.settings.attributes.freeText")}
                      </Text>
                    ) : (
                      <Group gap={4}>
                        {d.allowedValues.map((v) => (
                          <Badge key={v} color="gray" variant="light" size="sm">
                            {v}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td>{t("pages.settings.attributes.usageCount", { count: d.usageCount })}</Table.Td>
                  <Table.Td>
                    <Button size="xs" variant="default" onClick={() => setDialog({ target: d })}>
                      {t("common.edit")}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && (defs ?? []).length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("pages.settings.attributes.empty")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <AttributeDefModal
        target={dialog?.target ?? null}
        opened={dialog !== null}
        onClose={() => setDialog(null)}
      />
    </Stack>
  );
}

// ── Anlegen/Bearbeiten ───────────────────────────────────────────────────────

/**
 * Anlegen und Bearbeiten teilten sich vor Aufgabe-12-Review (Befund 2) fast
 * denselben Code — Unterschied war nur der Anfangszustand und ob
 * `useCreateAttributeDef` oder `useUpdateAttributeDef` feuert. `target` ist
 * die einzige Weiche: `null` → Anlegen-Dialog, sonst Bearbeiten. `opened`
 * steuert Sichtbarkeit separat, weil `target: null` sowohl „geschlossen" als
 * auch „Anlegen-Dialog offen" bedeuten kann (siehe `AttributeDialogState`
 * oben).
 */
function AttributeDefModal({
  target,
  opened,
  onClose,
}: {
  target: AttributeDefRef | null;
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const createDef = useCreateAttributeDef();
  const updateDef = useUpdateAttributeDef();
  const [name, setName] = useState("");
  const [allowedValues, setAllowedValues] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Nur beim Öffnen aus `target` neu befüllen — beim Schließen übernimmt
  // `handleClose` das Zurücksetzen, damit der nächste Öffnungsvorgang (egal
  // ob Anlegen oder ein anderer Eintrag) nie den alten Zustand kurz aufblitzen lässt.
  useEffect(() => {
    if (!opened) return;
    setName(target?.name ?? "");
    setAllowedValues(target?.allowedValues ?? []);
    setError(null);
  }, [opened, target]);

  function handleClose() {
    setName("");
    setAllowedValues([]);
    setError(null);
    onClose();
  }

  const mutation = target ? updateDef : createDef;

  async function handleSubmit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("pages.settings.attributes.nameRequired"));
      return;
    }
    try {
      if (target) {
        await updateDef.mutateAsync({ id: target.id, name: trimmed, allowedValues });
      } else {
        await createDef.mutateAsync({ name: trimmed, allowedValues });
      }
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pages.settings.attributes.saveError"));
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        target
          ? t("pages.settings.attributes.editTitle", { name: target.name })
          : t("pages.settings.attributes.createTitle")
      }
      size="sm"
      centered
    >
      <Stack gap="sm">
        <TextInput
          label={t("pages.settings.attributes.tableName")}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          autoFocus={target === null}
          required
        />
        <TagsInput
          label={t("pages.settings.attributes.tableAllowedValues")}
          description={t("pages.settings.attributes.allowedValuesHint")}
          value={allowedValues}
          onChange={setAllowedValues}
          placeholder={t("pages.settings.attributes.allowedValuesPlaceholder")}
        />
        {error && (
          <Alert color="red" title={t("common.error")}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={mutation.isPending}>
            {t("common.cancel")}
          </Button>
          <Button loading={mutation.isPending} onClick={() => void handleSubmit()}>
            {t("common.save")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
